import { AppIcon } from '../types/apps';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('ENTITY_TYPE_REGISTRY', { enabled: false });

interface EntityTypeMapping {
  typeName: string;
  appType: string;
  component: string;
  icon: AppIcon;
  canCreateInFolder: boolean;
  hiddenFromLists: boolean;
  creationCommand?: string;
  schema?: any;
}

// Hardcoded entity type definitions — previously seeded via m056 + m057 migrations.
// Add new custom entity types here instead of seeding them via DB migrations.
const STATIC_ENTITY_TYPE_DEFINITIONS: EntityTypeMapping[] = [
  {
    typeName: 'Transcript',
    appType: 'transcript',
    component: 'TranscriptApp',
    icon: { type: 'phosphor', phosphorIcon: 'Microphone', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'transcript_create',
  },
  {
    typeName: 'StoryCharacter',
    appType: 'entity-view',
    component: 'StoryCharacterView',
    icon: { type: 'phosphor', phosphorIcon: 'User', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'storycharacter_create',
  },
  {
    typeName: 'LegalCode',
    appType: 'entity-view',
    component: 'LegalCodeView',
    icon: { type: 'phosphor', phosphorIcon: 'Scale', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'legalcode_create',
  },
  {
    typeName: 'VerifiedSource',
    appType: 'entity-view',
    component: 'LegalCodeView',
    icon: { type: 'phosphor', phosphorIcon: 'Stamp', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'verifiedsource_create',
  },
  {
    typeName: 'ManuscriptReport',
    appType: 'entity-view',
    component: 'ManuscriptReportView',
    icon: { type: 'phosphor', phosphorIcon: 'FileText', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'manuscriptreport_create',
  },
  {
    typeName: 'ManuscriptDiscoveryReport',
    appType: 'entity-view',
    component: 'ManuscriptDiscoveryReportView',
    icon: { type: 'phosphor', phosphorIcon: 'MagnifyingGlass', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'manuscriptdiscoveryreport_create',
  },
  {
    typeName: 'NSFWPromptTemplate',
    appType: 'entity-view',
    component: 'NSFWPromptTemplateView',
    icon: { type: 'phosphor', phosphorIcon: 'Notebook', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'nsfwprompttemplate_create',
  },
  {
    typeName: 'CitationVerificationReport',
    appType: 'entity-view',
    component: 'CitationVerificationReportView',
    icon: { type: 'phosphor', phosphorIcon: 'BookBookmark', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'citationverificationreport_create',
  },
  {
    typeName: 'Rule',
    appType: 'entity-view',
    component: 'RuleView',
    icon: { type: 'phosphor', phosphorIcon: 'Scroll', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'rule_create',
  },
  {
    typeName: 'AppProject',
    appType: 'entity-view',
    component: 'AppProjectView',
    icon: { type: 'phosphor', phosphorIcon: 'Code', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'appproject_create',
    schema: {
      prompt: { type: 'string', required: true },
      appId: { type: 'string', required: true },
      iterations: { type: 'array' },
      status: { type: 'string', enum: ['generating', 'active', 'error', 'deprecated'] },
    },
  },
  {
    typeName: 'Codebase',
    appType: 'entity-view',
    component: 'CodebaseView',
    icon: { type: 'phosphor', phosphorIcon: 'Code', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: false,
    hiddenFromLists: false,
  },
  {
    typeName: 'Terminal',
    appType: 'terminal',
    component: 'TerminalApp',
    icon: { type: 'phosphor', phosphorIcon: 'Terminal', phosphorWeight: 'bold' } as AppIcon,
    canCreateInFolder: false,
    hiddenFromLists: false,
  },
  {
    typeName: 'Property',
    appType: 'entity-graph',
    component: 'EntityGraphEntityView',
    icon: { type: 'phosphor', phosphorIcon: 'House', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
  },
  {
    typeName: 'Company',
    appType: 'entity-graph',
    component: 'EntityGraphEntityView',
    icon: { type: 'phosphor', phosphorIcon: 'Buildings', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
  },
  {
    typeName: 'Deal',
    appType: 'entity-graph',
    component: 'EntityGraphEntityView',
    icon: { type: 'phosphor', phosphorIcon: 'Handshake', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
  },
  {
    typeName: 'LegalEntity',
    appType: 'entity-graph',
    component: 'EntityGraphEntityView',
    icon: { type: 'phosphor', phosphorIcon: 'Scales', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
  },
  {
    typeName: 'LegalFiling',
    appType: 'entity-graph',
    component: 'EntityGraphEntityView',
    icon: { type: 'phosphor', phosphorIcon: 'Stamp', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
  },
  {
    typeName: 'SecurityInstrument',
    appType: 'entity-graph',
    component: 'EntityGraphEntityView',
    icon: { type: 'phosphor', phosphorIcon: 'ChartPieSlice', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
  },
  {
    typeName: 'Transaction',
    appType: 'entity-graph',
    component: 'EntityGraphEntityView',
    icon: { type: 'phosphor', phosphorIcon: 'CurrencyDollar', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
  },
  {
    typeName: 'Insurance',
    appType: 'entity-graph',
    component: 'EntityGraphEntityView',
    icon: { type: 'phosphor', phosphorIcon: 'ShieldCheck', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
  },
  {
    typeName: 'Subscription',
    appType: 'entity-graph',
    component: 'EntityGraphEntityView',
    icon: { type: 'phosphor', phosphorIcon: 'ArrowsClockwise', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
  },
  {
    typeName: 'ExtractionRun',
    appType: 'entity-graph',
    component: 'EntityGraphEntityView',
    icon: { type: 'phosphor', phosphorIcon: 'MagnifyingGlass', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: false,
    hiddenFromLists: true,
  },
  // DatabaseApp — Table is the routable container; TableRow is a child entity
  // rendered inside a Table. Database.Connection and Database.SavedQuery are
  // external-database entities that route to the DatabaseApp home view.
  {
    typeName: 'Table',
    appType: 'database',
    component: 'DatabaseApp',
    icon: { type: 'phosphor', phosphorIcon: 'Table', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'table_create',
  },
  {
    typeName: 'TableRow',
    appType: 'database',
    component: 'DatabaseApp',
    icon: { type: 'phosphor', phosphorIcon: 'Rows', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: false,
    hiddenFromLists: true,
  },
  {
    typeName: 'Database.Connection',
    appType: 'database',
    component: 'DatabaseApp',
    icon: { type: 'phosphor', phosphorIcon: 'Database', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: false,
    hiddenFromLists: false,
  },
  {
    typeName: 'Database.SavedQuery',
    appType: 'database',
    component: 'DatabaseApp',
    icon: { type: 'phosphor', phosphorIcon: 'MagnifyingGlass', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: false,
    hiddenFromLists: true,
  },
  // VideoStudioApp — VideoProject is the routable timeline container.
  // File.VideoProxy / File.VideoWaveform / File.VideoThumbnail.Sprite are
  // internal derivatives created by the ingest worker and are hidden from the sidebar.
  {
    typeName: 'VideoProject',
    appType: 'videostudio',
    component: 'VideoStudioApp',
    icon: { type: 'phosphor', phosphorIcon: 'FilmReel', phosphorWeight: 'duotone' } as AppIcon,
    canCreateInFolder: true,
    hiddenFromLists: false,
    creationCommand: 'videoproject_create',
  },
];

class EntityTypeRegistry {
  private mappings = new Map<string, EntityTypeMapping>();
  private initialized = false;
  private listeners: Array<() => void> = [];

  loadEntityTypes(): void {
    if (this.initialized) return;

    for (const def of STATIC_ENTITY_TYPE_DEFINITIONS) {
      this.mappings.set(def.typeName, def);
      log.info(`[loadEntityTypes] Registered: ${def.typeName} → ${def.appType}`);
    }

    this.initialized = true;
    log.info(`[loadEntityTypes] ✅ Loaded ${this.mappings.size} hardcoded entity type definitions`);
  }

  /**
   * Register a single user-created EntityTypeDefinition entity at runtime.
   * Call this after creating a new entity type via entity_type_create.ts.
   */
  registerDefinition(entityTypeEntity: {
    skeleton: {
      typeName: string;
      viewConfig: { appType: string; component: string };
      icon: AppIcon;
      canCreateInFolder: boolean;
      hiddenFromLists: boolean;
      creationCommand?: string;
      schema?: any;
    };
  }): void {
    const { typeName, viewConfig, icon, canCreateInFolder, hiddenFromLists, creationCommand, schema } =
      entityTypeEntity.skeleton;

    const mapping: EntityTypeMapping = {
      typeName,
      appType: viewConfig.appType,
      component: viewConfig.component,
      icon,
      canCreateInFolder,
      hiddenFromLists,
      creationCommand,
      schema,
    };

    this.mappings.set(typeName, mapping);
    log.info(`[registerDefinition] Registered dynamic type: ${typeName} → ${viewConfig.appType}`);
    this.notifyListeners();
  }

  getAppTypeForEntity(entityType: string): string {
    const mapping = this.mappings.get(entityType);

    if (mapping) {
      return mapping.appType;
    }

    return this.getLegacyAppType(entityType);
  }

  /** Returns the appType only if this entity type has an explicit definition; null otherwise. */
  getRegisteredAppType(entityType: string): string | null {
    return this.mappings.get(entityType)?.appType ?? null;
  }

  getComponentForEntityType(entityType: string): string | null {
    return this.mappings.get(entityType)?.component || null;
  }

  isHiddenFromLists(entityType: string): boolean {
    return this.mappings.get(entityType)?.hiddenFromLists || false;
  }

  getAllEntityTypes(): EntityTypeMapping[] {
    return Array.from(this.mappings.values());
  }

  getFolderCreatableTypes(): EntityTypeMapping[] {
    return Array.from(this.mappings.values()).filter(m => m.canCreateInFolder);
  }

  private getLegacyAppType(entityType: string): string {
    if (entityType === 'Doc' || entityType === 'Document') return 'docs';
    if (entityType === 'Note' || entityType === 'Content.Note') return 'notes-v2';
    if (entityType === 'Canvas') return 'canvas-v2';
    if (entityType === 'Sheet' || entityType === 'Spreadsheet') return 'sheets-v2';
    if (entityType === 'Presentation') return 'presentations-v2';
    if (entityType === 'Project' || entityType === 'Area') return 'tasks';
    if (entityType === 'APIConnection') return 'api-connections';
    if (entityType === 'WebDocument') return 'webdocument';
    if (entityType === 'Space') return 'entityBrowser';
    if (entityType === 'Group') return 'group';
    if (entityType === 'Codebase') return 'entity-view';

    if (entityType?.startsWith('File.Image')) return 'mediaviewer';
    if (entityType?.startsWith('File.Document.PDF')) return 'pdfium';
    if (entityType?.startsWith('External.GoogleDrive')) return 'google-drive-file';
    if (entityType?.startsWith('External.LocalFile')) return 'local-file';

    if (entityType?.startsWith('Mail.')) return 'entity-view';

    return 'docs';
  }

  onTypesChanged(callback: () => void): () => void {
    this.listeners.push(callback);
    return () => {
      const idx = this.listeners.indexOf(callback);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (error) {
        log.error('[notifyListeners] Listener error:', error);
      }
    }
  }
}

export const entityTypeRegistry = new EntityTypeRegistry();
